import { v4 as uuidV4 } from 'uuid';
import type { HoldBookingPayload } from './queries';
import type { RuteroLuggageSelection } from '@bandido/shared/types';

export type HoldPayloadWithoutAttempt = Omit<HoldBookingPayload, 'attemptId'>;

export interface HoldAttemptIdentity {
  payloadIdentity: string;
  /** Identidad comercial sin el token efímero de la cotización. */
  logicalPayloadIdentity: string;
  /** Pedido exacto del primer envío; los retries no adoptan un token nuevo. */
  submittedPayload: HoldPayloadWithoutAttempt;
  attemptId: string;
  /** Contexto comercial local; nunca se agrega al payload del proveedor. */
  luggage?: RuteroLuggageSelection;
  /** El intento adoptado se recupera desde su snapshot privado del backend. */
  retryFromDurableSnapshot?: boolean;
}

export type HoldAttemptSubmission =
  | {
    kind: 'payload';
    payload: HoldBookingPayload;
  }
  | {
    kind: 'durable-retry';
    attemptId: string;
  };

export interface HoldAttemptHolder {
  current: HoldAttemptIdentity | null;
}

const CLEAN_TERMINAL_ATTEMPT_CODES = new Set([
  'RUTERO_PROVENANCE_EXPIRED',
  // La oferta venció según el vencimiento firmado por el proveedor: el hub
  // rechazó sin llamarlo. Rechazo limpio — el intento se libera; la cotización
  // entera la invalida aparte `requiresFreshSearch`.
  'RUTERO_OFFER_EXPIRED',
  'RUTERO_OFFER_SELECTION_CHANGED',
  'TOUR_SELECTION_UNAVAILABLE',
  'RUTERO_PROVIDER_POLICY',
  'RUTERO_HOLD_CLEAN_FAILED',
  // Faltó un dato del pasajero: el hub rechazó sin llegar al proveedor. El
  // asesor corrige el campo marcado y reintenta con un intento nuevo.
  'RUTERO_HOLD_MISSING_PASSENGER_FIELDS',
  'RUTERO_HOLD_RAIL_PASSENGER_COMPOSITION_MISMATCH',
  // La selección de opciones trae ids que la oferta no ofrece: el hub rechazó
  // sin tocar al proveedor. Se corrige la selección y se reintenta con un
  // intento nuevo.
  'RUTERO_HOLD_UNKNOWN_OFFER_OPTIONS',
  'RUTERO_HOLD_OCCUPANCY_PASSENGERS_MISMATCH',
  'RUTERO_HOLD_OCCUPANCY_CATEGORY_MISMATCH',
  'RUTERO_HOLD_SUPPLEMENT_LIMIT',
  'RUTERO_HOLD_SUPPLEMENT_DETAIL_REQUIRED',
  'RUTERO_HOLD_QUOTE_MISMATCH',
  'QUOTE_INPUT_REQUIRED',
  // Más unidades de un adicional que el `maxAllowed` de la oferta: el hub
  // rechazó sin tocar al proveedor. Se corrige la cantidad en la sección de
  // adicionales y se reintenta con un intento nuevo.
  'RUTERO_HOLD_OPTIONAL_ITEM_LIMIT',
  // No había contexto de markup durable para congelar el precio de venta: el
  // backend rechazó ANTES de llamar al hub, así que no hay nada reservado. El
  // asesor re-busca y el intento nuevo vuelve a capturar el contexto.
  'RUTERO_HOLD_MARKUP_CONTEXT_UNAVAILABLE',
  // El caso se valida antes del write-ahead y del proveedor: si cambió de
  // agencia o perdió su cliente, no existe ninguna operación que preservar.
  'RUTERO_BOOKING_CASE_NOT_FOUND',
  'RUTERO_BOOKING_CASE_WITHOUT_CLIENT',
]);

function canonicalize(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(canonicalize);
  if (!value || typeof value !== 'object') return value;
  return Object.keys(value as Record<string, unknown>)
    .sort()
    .reduce<Record<string, unknown>>((acc, key) => {
      const item = (value as Record<string, unknown>)[key];
      if (item !== undefined) acc[key] = canonicalize(item);
      return acc;
    }, {});
}

export function holdPayloadIdentity(payload: HoldPayloadWithoutAttempt): string {
  return JSON.stringify(canonicalize(payload));
}

/**
 * El quote token liga el pedido al quote, pero no es una elección del asesor:
 * un refetch puede rotarlo mientras se está reintentando el MISMO envío.
 */
export function holdLogicalPayloadIdentity(
  payload: HoldPayloadWithoutAttempt,
  luggage?: RuteroLuggageSelection,
): string {
  const { quoteToken: _quoteToken, ...logicalPayload } = payload;
  return JSON.stringify(canonicalize({ ...logicalPayload, ...(luggage ? { luggage } : {}) }));
}

export function holdAttemptMatchesLuggage(
  attempt: HoldAttemptIdentity | null,
  luggage?: RuteroLuggageSelection,
): boolean {
  return !attempt || (attempt.luggage?.checked === luggage?.checked &&
    attempt.luggage?.carryOn === luggage?.carryOn);
}

/** Recovery is an unchanged operation, not a new quote-authorized purchase. */
export function existingLuggageHoldRecovery(
  attempt: HoldAttemptIdentity | null,
  payload: HoldPayloadWithoutAttempt | null,
  luggage: RuteroLuggageSelection | null | undefined,
  retryAllowed: boolean,
): HoldAttemptIdentity | null {
  return retryAllowed && attempt?.luggage && payload && luggage &&
    attempt.logicalPayloadIdentity === holdLogicalPayloadIdentity(payload, luggage)
    ? attempt : null;
}

/**
 * `crypto.randomUUID` solo existe en contextos seguros: servido por http en
 * una IP de la red (el compose levanta vite con `--host 0.0.0.0`) no está, y
 * tirar dentro del clic dejaba el botón mudo. `uuid` v4 vale igual.
 */
export function defaultAttemptIdFactory(): string {
  if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID();
  return uuidV4();
}

export function resolveHoldAttemptIdentity(
  current: HoldAttemptIdentity | null,
  payload: HoldPayloadWithoutAttempt,
  generateId: () => string = defaultAttemptIdFactory,
  luggage?: RuteroLuggageSelection,
): HoldAttemptIdentity {
  if (!holdAttemptMatchesLuggage(current, luggage)) {
    throw new Error('Resolve the existing hold before changing luggage');
  }
  const payloadIdentity = holdPayloadIdentity(payload);
  const logicalPayloadIdentity = holdLogicalPayloadIdentity(payload, luggage);
  if (current?.logicalPayloadIdentity === logicalPayloadIdentity) return current;
  return {
    payloadIdentity,
    logicalPayloadIdentity,
    submittedPayload: payload,
    attemptId: generateId(),
    ...(luggage ? { luggage: { ...luggage } } : {}),
  };
}

export function resolveHoldAttemptFromHolder(
  holder: HoldAttemptHolder,
  payload: HoldPayloadWithoutAttempt,
  generateId: () => string = defaultAttemptIdFactory,
  luggage?: RuteroLuggageSelection,
): HoldAttemptIdentity {
  const next = resolveHoldAttemptIdentity(holder.current, payload, generateId, luggage);
  holder.current = next;
  return next;
}

export function clearHoldAttemptHolder(holder: HoldAttemptHolder): void {
  holder.current = null;
}

export function holdAttemptSubmission(
  attempt: HoldAttemptIdentity,
): HoldAttemptSubmission {
  if (attempt.retryFromDurableSnapshot) {
    return { kind: 'durable-retry', attemptId: attempt.attemptId };
  }
  return {
    kind: 'payload',
    payload: { ...attempt.submittedPayload, attemptId: attempt.attemptId },
  };
}

export function adoptExistingHoldAttempt(
  holder: HoldAttemptHolder,
  expectedPayloadIdentity: string,
  attemptId: string,
): boolean {
  if (holder.current?.payloadIdentity !== expectedPayloadIdentity) return false;
  holder.current = {
    ...holder.current,
    attemptId,
    retryFromDurableSnapshot: true,
  };
  return true;
}

export function shouldResetHoldAttemptAfterError(code: string | undefined): boolean {
  return CLEAN_TERMINAL_ATTEMPT_CODES.has(code ?? '');
}
